"use client";

import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useEffect } from "react";
import { toast } from "@/components/ui/snackbar";
import { shouldSkipErrorToast } from "@/lib/errors";
import { graphqlErrorMessage } from "@/lib/graphql/client";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";

export type AutoRechargeSettings = {
  enabled: boolean;
  // Micro-USD values are strings to avoid GraphQL's 32-bit Int overflow.
  threshold_micro?: string | null;
  target_micro?: string | null;
  monthly_limit_micro?: string | null;
};

export type BillingOverview = {
  stripe_mode: string;
  billing_complete: boolean;
  billing_suspended: boolean;
  /**
   * True when the local billing record is out of sync with Stripe — e.g. a card
   * is attached in Stripe but not reflected locally, or billing is marked complete
   * but Stripe shows no payment methods. When true, a reconcile action is available.
   */
  needs_reconciliation: boolean;
  default_payment_method_id?: string | null;
  /**
   * Prepaid credit balance in micro-USD (1e-6 USD). May be negative. Returned as
   * a string to avoid GraphQL's 32-bit Int overflow for large balances.
   */
  credit_balance_micro: string;
  /**
   * Count of positive credit ledger entries (topups and positive adjustments).
   * Used by the low-credit promo UI.
   */
  positive_credit_entry_count: number;
  monthly_spend_limit_micro?: string | null;
  /** Absolute usage spend this calendar month (micro-USD string). */
  monthly_spend_used_micro: string;
  /** Lifetime free credit granted (welcome + admin grants), micro-USD string. */
  lifetime_free_granted_micro: string;
  auto_recharge: AutoRechargeSettings;
  invoices: Array<{
    id: string;
    number?: string | null;
    status?: string | null;
    total?: number | null;
    amount_due?: number | null;
    amount_paid?: number | null;
    hosted_invoice_url?: string | null;
    invoice_pdf?: string | null;
    created?: string | null;
  }>;
  payment_methods: Array<{
    id: string;
    type?: string | null;
    brand?: string | null;
    last4?: string | null;
    exp_month?: number | null;
    exp_year?: number | null;
    link_email?: string | null;
    is_default: boolean;
  }>;
};

export type BillingOverviewQuery = {
  billing_overview: BillingOverview;
};

const billingOverviewDocument = gql`
  query BillingOverview {
    billing_overview {
      stripe_mode
      billing_complete
      billing_suspended
      needs_reconciliation
      default_payment_method_id
      credit_balance_micro
      positive_credit_entry_count
      monthly_spend_limit_micro
      monthly_spend_used_micro
      lifetime_free_granted_micro
      auto_recharge {
        enabled
        threshold_micro
        target_micro
        monthly_limit_micro
      }
      invoices {
        id
        number
        status
        total
        amount_due
        amount_paid
        hosted_invoice_url
        invoice_pdf
        created
      }
      payment_methods {
        id
        type
        brand
        last4
        exp_month
        exp_year
        link_email
        is_default
      }
    }
  }
`;

/** Dedupe toasts across multiple `useBillingOverview` subscribers. */
let lastBillingOverviewErrorToastAt = 0;

export const useBillingOverview = (
  options?: Omit<UseQueryOptions<BillingOverviewQuery, Error, BillingOverviewQuery, QueryKey>, "queryKey" | "queryFn">,
) => {
  const query = useGraphqlQuery<BillingOverviewQuery>({
    queryKey: ["billing-overview"],
    document: billingOverviewDocument,
    retry: false,
    // Payment methods are added via a full-page redirect to the billing portal.
    // When the user returns to /manage/billing (back button / bfcache restore /
    // tab refocus), the module-level QueryClient may still hold the pre-portal
    // cache. The global 60s staleTime would then suppress a refetch, leaving the
    // page showing "no payment method" even though a card was just added.
    // Always treat this query as stale so returning to the page refetches the
    // live payment-method state.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    ...options,
  });

  useEffect(() => {
    if (!query.isError || !query.error || query.errorUpdatedAt === 0) return;
    if (query.errorUpdatedAt === lastBillingOverviewErrorToastAt) return;
    lastBillingOverviewErrorToastAt = query.errorUpdatedAt;
    if (shouldSkipErrorToast(query.error)) return;
    toast.error(graphqlErrorMessage(query.error, "Failed to load billing."));
  }, [query.isError, query.error, query.errorUpdatedAt]);

  return query;
};
